import React from 'react';
import { Outlet, useNavigate } from '@tanstack/react-router';
import { Page } from '@chatsdet/ui';
import {
  Buildings,
  Pulse,
  Briefcase,
  Clock,
  CloudArrowUp,
  Flag,
  ArrowsOut,
  Sliders,
  ListChecks,
  Info,
  ShieldCheck,
  FlagBanner,
  ChartLine,
  ChartBar,
  GitBranch,
  Gauge,
  Lightbulb,
  Chat,
  Bell,
  Rocket,
  Robot,
  Scroll,
  UsersThree,
  CurrencyDollar,
  EnvelopeSimple,
} from '@phosphor-icons/react';
import { useSubNav } from '../../../hooks/useSubNav';
import { useBreadcrumbs } from '../../../contexts/BreadcrumbsContext';
import type { SubNavConfig } from '../../../contexts/SubNavContext';

const SUPERADMIN_NAV: SubNavConfig = {
  title: 'Superadmin',
  basePath: '/superadmin',
  items: [
    { id: 'organizations', label: 'Organizations', icon: <Buildings size={16} />, to: '/superadmin/organizations', section: 'Organizations' },
    { id: 'users', label: 'Users', icon: <UsersThree size={16} />, to: '/superadmin/users', section: 'Organizations' },
    { id: 'marketing-leads', label: 'Marketing Leads', icon: <EnvelopeSimple size={16} />, to: '/superadmin/marketing-leads', section: 'Organizations' },
    { id: 'jobs', label: 'Jobs', icon: <Briefcase size={16} />, to: '/superadmin/jobs', section: 'Jobs' },
    { id: 'agent-runs', label: 'Agent Runs', icon: <Pulse size={16} />, to: '/superadmin/agent-runs', section: 'Jobs' },
    { id: 'scheduled', label: 'Scheduled Jobs', icon: <Clock size={16} />, to: '/superadmin/scheduled', section: 'Jobs' },
    { id: 'benchmark', label: 'Benchmark', icon: <ChartBar size={16} />, to: '/superadmin/benchmark', section: 'Observability' },
    { id: 'audit-log', label: 'Audit Log', icon: <Scroll size={16} />, to: '/superadmin/audit-log', section: 'Observability' },
    { id: 'builder-sessions', label: 'Builder Sessions', icon: <Robot size={16} />, to: '/superadmin/builder-sessions', section: 'Observability' },
    { id: 'metrics', label: 'Metrics', icon: <ChartLine size={16} />, to: '/superadmin/metrics', section: 'Observability' },
    { id: 'cache-telemetry', label: 'Cache Telemetry', icon: <ChartLine size={16} />, to: '/superadmin/cache-telemetry', section: 'Observability' },
    { id: 'token-usage', label: 'Token Usage', icon: <CurrencyDollar size={16} />, to: '/superadmin/token-usage', section: 'Observability' },
    { id: 'verifications', label: 'Verifications', icon: <ShieldCheck size={16} />, to: '/superadmin/verifications', section: 'Observability' },
    { id: 'user-events', label: 'User Events', icon: <Bell size={16} />, to: '/superadmin/user-events', section: 'Observability' },
    { id: 'workflows', label: 'Workflows', icon: <GitBranch size={16} />, to: '/superadmin/workflows', section: 'Configuration' },
    { id: 'feature-flags', label: 'Feature Flags', icon: <Flag size={16} />, to: '/superadmin/feature-flags', section: 'Configuration' },
    { id: 'knobs', label: 'Knobs', icon: <Sliders size={16} />, to: '/superadmin/knobs', section: 'Configuration' },
    { id: 'hints', label: 'Hints', icon: <Lightbulb size={16} />, to: '/superadmin/hints', section: 'Configuration' },
    { id: 'org-quotas', label: 'Org Quotas', icon: <Gauge size={16} />, to: '/superadmin/org-quotas', section: 'Configuration' },
    { id: 'support', label: 'Support Chat', icon: <Chat size={16} />, to: '/superadmin/support', section: 'Configuration' },
    { id: 'deployment-alerts', label: 'Deployment Alerts', icon: <Rocket size={16} />, to: '/superadmin/deployment-alerts', section: 'Configuration' },
    { id: 'test-templates', label: 'Test Templates', icon: <ListChecks size={16} />, to: '/superadmin/test-templates', section: 'Configuration' },
    { id: 'trace-uploads', label: 'Trace Uploads', icon: <CloudArrowUp size={16} />, to: '/superadmin/trace-uploads', section: 'Tools' },
    { id: 'dedup', label: 'Dedup Inspector', icon: <ArrowsOut size={16} />, to: '/superadmin/dedup', section: 'Tools' },
    { id: 'issue-reviews', label: 'Flagged Issues', icon: <FlagBanner size={16} />, to: '/superadmin/issue-reviews', section: 'Tools' },
    { id: 'about', label: 'About', icon: <Info size={16} />, to: '/superadmin/about', section: 'System' },
  ],
};

export function SuperadminLayout() {
  const navigate = useNavigate();

  useSubNav(SUPERADMIN_NAV);

  useBreadcrumbs([
    {
      label: 'Home',
      onPress: () => navigate({ to: '/' }),
    },
    {
      label: 'Superadmin',
    },
  ]);

  return (
    <Page>
      <Outlet />
    </Page>
  );
}
